import React, { createContext, useContext, useState, useCallback, ReactNode, FC } from 'react';

// Create a context for the screen reader announcer
interface ScreenReaderAnnouncerContextType {
  announceToScreenReader: (message: string) => void;
}

const ScreenReaderAnnouncerContext = createContext<ScreenReaderAnnouncerContextType | undefined>(undefined);

// Provider component that will wrap parts of the app that need screen reader announcements
interface ScreenReaderAnnouncerProviderProps {
  children: ReactNode;
}

export const ScreenReaderAnnouncerProvider: FC<ScreenReaderAnnouncerProviderProps> = ({ children }) => {
  const [announcement, setAnnouncement] = useState('');

  // Function to announce messages to screen readers
  const announceToScreenReader = useCallback((message: string) => {
    // Clear first
    setAnnouncement('');

    // Use requestAnimationFrame to ensure the DOM has been updated before setting new content
    requestAnimationFrame(() => {
      setAnnouncement(message);
    });
  }, []);

  return (
    <ScreenReaderAnnouncerContext.Provider value={{ announceToScreenReader }}>
      {children}
      {/* Live region for screen reader announcements */}
      <div
        aria-live="polite"
        className="sr-only"
        role="status"
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: '0',
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: '0'
        }}
      >
        {announcement}
      </div>
    </ScreenReaderAnnouncerContext.Provider>
  );
};

// Custom hook to use the screen reader announcer
export const useScreenReaderAnnouncer = (): ScreenReaderAnnouncerContextType => {
  const context = useContext(ScreenReaderAnnouncerContext);

  if (context === undefined) {
    throw new Error('useScreenReaderAnnouncer must be used within a ScreenReaderAnnouncerProvider');
  }

  return context;
};
